import { useEffect, useState, useMemo, useCallback } from 'react';
import { fetchAllProducts } from '../api/strapi/products';
import { fetchAllServices } from '../api/strapi/services';
import { fetchAllRandD } from '../api/strapi/randd';
import { fetchAllCompanies } from '../api/strapi/companies';
import { fetchAllIndustries } from '../api/strapi/industries';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';

/**
 * Custom hook pour la recherche globale sur tous les contenus
 * Fetch les données depuis Strapi et fournit une fonction de recherche
 */
export const useGlobalSearch = () => {
  const { language } = useLanguage();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fonction pour construire l'URL d'un item selon sa catégorie
  const buildItemLink = useCallback((item, category) => {
    if (item.link) {
      return item.link;
    }

    const slug = item.slug;
    if (!slug) return '/';

    switch (category) {
      case 'products':
        return getLocalizedPath(`/info/produits/${slug}`, language);
      case 'industries':
        return getLocalizedPath(`/info/industries/${slug}`, language);
      case 'services':
        return getLocalizedPath(`/info/services/${slug}`, language);
      case 'rds':
        return getLocalizedPath(`/info/r-and-d/${slug}`, language);
      case 'companies':
        return getLocalizedPath(`/info/company/${slug}`, language);
      default:
        return '/';
    }
  }, [language]);

  // Charger toutes les données au montage
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch en parallèle de tous les contenus
        const [productsRes, servicesRes, randdRes, companiesRes, industriesRes] = await Promise.all([
          fetchAllProducts({ locale: language, fallbackLocale: 'fr' }),
          fetchAllServices({ locale: language, fallbackLocale: 'fr' }),
          fetchAllRandD({ locale: language, fallbackLocale: 'fr' }),
          fetchAllCompanies({ locale: language, fallbackLocale: 'fr' }),
          fetchAllIndustries({ locale: language, fallbackLocale: 'fr' }),
        ]);

        const items = [];

        // Traiter les produits
        if (productsRes.ok && Array.isArray(productsRes.data)) {
          productsRes.data.forEach((item) => {
            items.push({
              id: item.documentId || item.id,
              title: item.title || 'Untitled',
              slug: item.slug,
              metaDescription: item.metaDescription || item.description || '',
              metaImage: item.metaImage,
              category: 'products',
              categoryLabel: 'Product',
              link: buildItemLink(item, 'products'),
            });
          });
        }

        // Traiter les services
        if (servicesRes.ok && Array.isArray(servicesRes.data)) {
          servicesRes.data.forEach((item) => {
            items.push({
              id: item.documentId || item.id,
              title: item.title || 'Untitled',
              slug: item.slug,
              metaDescription: item.metaDescription || item.description || '',
              metaImage: item.metaImage,
              category: 'services',
              categoryLabel: 'Service',
              link: buildItemLink(item, 'services'),
            });
          });
        }

        // Traiter les R&D
        if (randdRes.ok && Array.isArray(randdRes.data)) {
          randdRes.data.forEach((item) => {
            items.push({
              id: item.documentId || item.id,
              title: item.title || 'Untitled',
              slug: item.slug,
              metaDescription: item.metaDescription || item.description || '',
              metaImage: item.metaImage,
              category: 'rds',
              categoryLabel: 'R&D',
              link: buildItemLink(item, 'rds'),
            });
          });
        }

        // Traiter les companies
        if (companiesRes.ok && Array.isArray(companiesRes.data)) {
          companiesRes.data.forEach((item) => {
            items.push({
              id: item.documentId || item.id,
              title: item.title || 'Untitled',
              slug: item.slug,
              metaDescription: item.metaDescription || item.description || '',
              metaImage: item.metaImage,
              category: 'companies',
              categoryLabel: 'Company',
              link: buildItemLink(item, 'companies'),
            });
          });
        }

        // Traiter les industries
        if (industriesRes.ok && Array.isArray(industriesRes.data)) {
          industriesRes.data.forEach((item) => {
            items.push({
              id: item.documentId || item.id,
              title: item.title || 'Untitled',
              slug: item.slug,
              metaDescription: item.metaDescription || item.description || '',
              metaImage: item.metaImage,
              category: 'industries',
              categoryLabel: 'Industry',
              link: buildItemLink(item, 'industries'),
            });
          });
        }

        setAllItems(items);
      } catch (err) {
        console.error('Error fetching search data:', err);
        setError(err.message || 'Failed to load search data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [language, buildItemLink]);

  // Fonction de recherche avec filtrage
  const search = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // Résultats filtrés avec memoization
  const results = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return {
        products: [],
        services: [],
        rds: [],
        companies: [],
        industries: [],
        total: 0,
      };
    }

    const normalizedQuery = searchQuery.toLowerCase().trim();

    const filtered = allItems.filter((item) => {
      const titleMatch = item.title?.toLowerCase().includes(normalizedQuery);
      const descMatch = item.metaDescription?.toLowerCase().includes(normalizedQuery);
      return titleMatch || descMatch;
    });

    // Grouper par catégorie
    const grouped = {
      products: filtered.filter((item) => item.category === 'products'),
      services: filtered.filter((item) => item.category === 'services'),
      rds: filtered.filter((item) => item.category === 'rds'),
      companies: filtered.filter((item) => item.category === 'companies'),
      industries: filtered.filter((item) => item.category === 'industries'),
      total: filtered.length,
    };

    return grouped;
  }, [searchQuery, allItems]);

  // Clear la recherche
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return {
    search,
    searchQuery,
    results,
    loading,
    error,
    clearSearch,
    totalItems: allItems.length,
  };
};

export default useGlobalSearch;
